import * as E from 'fp-ts/es6/Either'
import * as TE from 'fp-ts/es6/TaskEither'
import * as T from 'fp-ts/es6/Task'
import * as A from 'fp-ts/es6/Array'
import { flow } from 'fp-ts/es6/function'
import { pipe } from 'fp-ts/es6/pipeable'
import { ApolloClient, ApolloError, FetchResult } from '@apollo/client'
import {
  PartnerUserRole,
  SendPartnerUserInviteDocument,
  SendPartnerUserInviteInput,
  SendPartnerUserInviteMutation,
  SendPartnerUserInviteMutationVariables,
} from 'generated/graphql'
import { doMutate } from 'util/apollo_client_helper'
import { PARTNER_ID_HEADER_KEY } from 'util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import { EmailAddress, PartnerAccountId } from '@amzn/aapn-type-guards'
import { getMetricsPublisherAsync, publishCounter } from '@amzn/aapn-front-end-metrics'

// === Constants ===

const MAX_ENTRIES = 500
export const BULK_INVITED_USER_NAME = 'Invited User'

// ===== Types =====
type EntryValidationFailure = 'Invalid' | 'TooManyEntries'

export type Invitation = { email: string; role: PartnerUserRole }
export type InvalidEntry = { value: string; reason: EntryValidationFailure }
export type InvalidEntries = {
  [key in EntryValidationFailure]: InvalidEntry[]
}

// ===== Data Entry and Validation =====

export const parseListOfInvitees: (inviteesInput: string) => string[] = flow(
  (inviteesInput: string) => {
    if (inviteesInput === '') {
      return []
    } else {
      return inviteesInput.split(/[\s;,]+/)
    }
  },
  A.map((s) => s.trim()),
  A.filter((s) => s !== ''),
)

const validateInvitee = (invitee: string): E.Either<InvalidEntry, EmailAddress> => {
  return pipe(
    EmailAddress.decode(invitee),
    E.mapLeft((): InvalidEntry => ({ value: invitee, reason: 'Invalid' })),
  )
}

const convertToInvite = (e: EmailAddress): Invitation => ({
  email: e,
  role: PartnerUserRole.Associate,
})

export const parseAndValidateListOfInvitations = (invitees: string[]): { validInvitations: Invitation[]; invalidRows: InvalidEntries } => {
  return pipe(
    //prettier-break
    Array.from(new Set(invitees)),
    A.map(validateInvitee),
    A.separate,
    (sep) => {
      const [withinLimit, overflow] = A.splitAt(MAX_ENTRIES)(sep.right)
      const overflowInvalidEntries = overflow.map((e: EmailAddress): InvalidEntry => ({ value: e, reason: 'TooManyEntries' }))

      return {
        left: {
          Invalid: sep.left,
          TooManyEntries: overflowInvalidEntries,
        },
        right: withinLimit,
      }
    },
    (sep) => ({
      validInvitations: sep.right.map(convertToInvite),
      invalidRows: sep.left,
    }),
  )
}

// ===== Invite Submission Process =====

const createUserInviteInput = (i: Invitation): SendPartnerUserInviteInput => ({ email: i.email, name: BULK_INVITED_USER_NAME, partnerUserRole: i.role })

const parseSubmissionResponse = (response: FetchResult<SendPartnerUserInviteMutation>): E.Either<Error, string> => {
  if (response.data?.sendPartnerUserInvite?.id) {
    return E.right(response.data?.sendPartnerUserInvite?.id)
  } else {
    return E.left(new Error('Failed to extract inviteId'))
  }
}

const submitIndividualInvite = (
  apolloClient: ApolloClient<object>,
  input: SendPartnerUserInviteInput,
  partnerAccountId: PartnerAccountId,
): TE.TaskEither<ApolloError, FetchResult<SendPartnerUserInviteMutation>> =>
  doMutate<SendPartnerUserInviteMutation, SendPartnerUserInviteMutationVariables>(apolloClient, {
    mutation: SendPartnerUserInviteDocument,
    variables: {
      input,
    },
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
      linkOptions: {
        retryLink: {
          enableRetries: false,
        },
        timeoutLink: {
          enableTimeout: false,
        },
      },
    },
  })

export const submitBulkUserInvites = (
  apolloClient: ApolloClient<object>,
  invitations: Invitation[],
  partnerAccountId: PartnerAccountId,
  invitesSentWithErrors: () => void,
): T.Task<E.Either<Error[], string[]>> => {
  return pipe(
    invitations,
    A.map(createUserInviteInput),
    A.map((i) => submitIndividualInvite(apolloClient, i, partnerAccountId)),
    A.map(TE.chainEitherK(parseSubmissionResponse)),
    A.array.sequence(T.task),
    T.map(A.separate),
    T.map(({ left, right }) => {
      if (left.length > 0) {
        if (right.length > 0) {
          invitesSentWithErrors()
        }
        return E.left(left)
      }
      return E.right(right)
    }),
    T.chainFirst((either) => {
      if (E.isRight(either)) {
        return pipe(
          TE.tryCatch(
            () => getMetricsPublisherAsync('BulkUserInvite'),
            () => new Error('Failed getting metrics agent'),
          ),
          TE.map((p) => {
            publishCounter('invocation', 1)(p)
            publishCounter('inviteCount', invitations.length)(p)
            return null
          }),
        )
      }
      return T.of(null)
    }),
  )
}
